<template lang="pug">
#standard-account-register(v-cloak)
  .box
    .box-header.with-border.register-header
      .title
        span 一般ユーザー登録
    .box-body.register-body
      FormInput(v-model="userId" key-name="ユーザーID（ログインID）" name="user-id" type="text" placeholder="半角小文字英数で入力してください" :maxlength="100" 
        :validation-rules="_userIdValidationRules" @change="onChangeUserId" required)
        template
          span.warning-message ユーザーIDはメールアドレスのみ使用できます。
      FormInput(v-if="!existAccount" v-model="confirmUserId" key-name="ユーザーID（ログインID）（確認）" name="confirm-user-id"
        type="text" placeholder="確認のため同じ内容を入力してください"
        :maxlength="100" :validation-rules="{ required: true, max: 100, email: true, non_uppercase:true }" required)

      .password(v-if="!existAccount")
        FormInputPassword(v-model="password" key-name="パスワード" name="password" placeholder="半角英数字8文字以上で設定してください" :maxlength="100"
          :validation-rules="{ required: true, half_alpha_num:true, min: 8, max: 100, equal_value: confirmPassword }" required)
        FormInputPassword(v-model="confirmPassword" key-name="パスワード（確認）" name="confirm-password" placeholder="確認のため同じ内容を入力してください"
          :maxlength="100" :validation-rules="{ required: true, half_alpha_num:true, min: 8, max: 100 }" required)

      .account-informations(v-if="!existAccount")
        FormInput(v-model="name" key-name="氏名" name="user-name" type="text" placeholder="氏名" :maxlength="32" 
          :validation-rules="{ required: true, max: 32 }" required)
        FormInput(v-model="kana" key-name="氏名（かな）" name="user-kana" type="text" placeholder="氏名（かな）" :maxlength="32"
          :validation-rules="{ required: true, max: 32, hiragana: true }" required)
        FormInput(v-model="tel" key-name="電話番号" name="user-tel" type="text" placeholder="電話番号" :maxlength="13"
          :validation-rules="{ required: true, max: 13, tel: true }" :styles="{ width: '340px' }" required)
        FormInput(v-model="mail" key-name="メールアドレス" name="mail" type="text" placeholder="半角小文字英数で入力してください" :maxlength="100"
          :validation-rules="{ required: true, max: 100, email: true, non_uppercase:true, equal_value: confirmMail }" required)
          template
            .warning-message.d-flex.flex-column
              span メールアドレスはユーザーIDとは別のメールアドレスを設定できます。
              span メールアドレスは弊社からのお知らせ・お問い合わせ等でご連絡させていただくメールアドレスになります。
        FormInput(v-model="confirmMail" key-name="メールアドレス（確認）" name="confirm-mail" type="text" placeholder="確認のため同じ内容を入力してください"
          :maxlength="100" :validation-rules="{ required: true, max: 100, email: true, non_uppercase:true }" required)
      .mb-2
      FormInputStandardUserFunctions(v-model="standardUserFunctionsModel")
      FormInputPermissionResidentGroup(v-if="_useFamilyService" v-model="residentGroups")
      FormInputPermissionOtherUserGroup(v-if="_useFamilyService" v-model="otherUserGroups")
      FormInputShowMessage(v-model="showMessages")

      .register-row(v-show="errorMessage")
        .register-group
          .register-value
            .error-message {{ errorMessage }}

    .box-footer.with-border.register-footer
      ValidationAlert(v-model="showValidationAlert")
      .buttons
        button.btn.update-btn(@click="showConfirmUpdateModal()" :disabled="disableRegister" :class="{ 'disable': disableRegister }") 登録
        button.btn.cancel-btn(@click="gotoStandardAccountList()") キャンセル

  OkCancelModal(v-model="showRegisterModal" title="一般ユーザー情報を登録しますか？" @ok="registerAccount")
  OkModal(v-model="showRegisterSuccessModal" title="登録に成功しました。" @ok="gotoStandardAccountList")

  OkModal(v-model="showDisableRegisterModal" title="同じユーザーIDが本施設に既に登録されているため登録できません。")
  OkModal(v-model="showDisablePasswordModal" title="同じユーザーIDが他施設に既に登録されているため他施設で入力済みの情報は入力できません。")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";

import CustomValidate from "@/commons/utils/CustomValidate";
import { PcUrl } from "@/commons/constants/PcUrl";
import SystemError from "@/commons/models/SystemError";
import StandardAccountsService, { StandardAccountsPostModel } from "@/commons/services/StandardAccounts";
import { ShowMessage } from "@/commons/services/Authorization";
import ResidentGroupsService, { ResidentGroupsGetModel } from "@/commons/services/ResidentGroups";
import OtherUserGroupsService from "@/commons/services/OtherUserGroups";
import OtherUserGroupModel from "@/commons/models/OtherUserGroup";
import OtherUserGroupTypesService from "@/commons/services/OtherUserGroupTypes";
import OtherUserGroupTypeModel from "@/commons/models/OtherUserGroupType";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import ValidationAlert from "@/pc/basics/ValidationAlertByValidator.vue";
import FormInput from "@/pc/components/forms/FormInput.vue";
import FormInputPassword from "@/pc/components/forms/FormInputPassword.vue";
import FormInputShowMessage from "@/pc/components/forms/FormInputShowMessage.vue";
import FormInputPermissionResidentGroup, { PermissionResidentGroupModel } from "@/pc/components/forms/FormInputPermissionResidentGroup.vue";
import FormInputPermissionOtherUserGroup, { PermissionOtherUserGroupModel } from "@/pc/components/forms/FormInputPermissionOtherUserGroup.vue";
import FormInputStandardUserFunctions, { StandardUserFunctionsModel } from "@/pc/containers/settings/FormInputStandardUserFunctions.vue";

/**
 * ### 一般ユーザー登録画面コンポーネント
 */
@Component({
  $_veeValidate: { validator: "new" },
  components: {
    OkCancelModal,
    OkModal,
    PageLoading,
    ValidationAlert,
    FormInput,
    FormInputPassword,
    FormInputShowMessage,
    FormInputPermissionResidentGroup,
    FormInputPermissionOtherUserGroup,
    FormInputStandardUserFunctions
  }
})
export default class StandardAccountRegister extends Vue {
  /** 一般アカウントサービス */
  @Inject("StandardAccountsService") standardAccountsService!: StandardAccountsService;
  /** 利用者グループサービス */
  @Inject("ResidentGroupsService") residentGroupsService!: ResidentGroupsService;
  /** 施設関係者グループサービス */
  @Inject("OtherUserGroupsService") otherUserGroupsService!: OtherUserGroupsService;
  /** 施設関係者グループ種類サービス */
  @Inject("OtherUserGroupTypesService") otherUserGroupTypesService!: OtherUserGroupTypesService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 登録確認モーダル表示可否 */
  showRegisterModal: boolean = false;
  /** 登録完了モーダル表示可否 */
  showRegisterSuccessModal: boolean = false;

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /** ユーザーID（ログインID） */
  userId: string = "";
  /** ユーザーID（ログインID）（確認） */
  confirmUserId: string = "";
  /** パスワード */
  password: string = "";
  /** パスワード（確認） */
  confirmPassword: string = "";
  /** 名前 */
  name: string = "";
  /** 名前かな */
  kana: string = "";
  /** 電話番号 */
  tel: string = "";
  /** メールアドレス */
  mail: string = "";
  /** メールアドレス（確認） */
  confirmMail: string = "";

  /** 登録不可メッセージモーダル表示可否 */
  showDisableRegisterModal: boolean = false;
  /** 登録可否 */
  disableRegister: boolean = false;
  /** 登録チェック済みか否か */
  registerChecked: boolean = false;

  /** パスワード入力不可メッセージモーダル表示可否 */
  showDisablePasswordModal: boolean = false;
  /** アカウント登録済みか */
  existAccount: boolean = false;
  /** ユーザーIDチェック済みか否か */
  userIdChecked: boolean = false;

  /** 使用可能機能Model */
  standardUserFunctionsModel: StandardUserFunctionsModel | null = null;
  /** 権限(利用者グループ) */
  residentGroups: Array<PermissionResidentGroupModel> = [];
  /** 権限(施設関係者グループ) */
  otherUserGroups: Array<PermissionOtherUserGroupModel> = [];
  /** 権限(メッセージ送信の下書きと履歴) */
  showMessages: ShowMessage = ShowMessage.ALL;
  /** 登録日時 */
  updated: number = 0;

  /** エラーメッセージ */
  errorMessage: string = "";

  get _useFamilyService(): boolean {
    return userStore.facility.useFamilyService;
  }

  get _userIdValidationRules(): Object {
    if (this.existAccount == true) {
      return { required: true, max: 100, email: true, non_uppercase: true };
    } else {
      return { required: true, max: 100, email: true, non_uppercase: true, equal_value: this.confirmUserId };
    }
  }

  /**
   * ### vue lifecycle created
   */
  public created() {
    CustomValidate.addCustomValidate(this.$validator);
    return;
  }

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    await this.createView();
    return;
  }

  /**
   * ### 画面表示情報を作成する
   */
  public async createView(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    this.userId = "";
    this.userId = "";
    this.confirmUserId = "";
    this.password = "";
    this.confirmPassword = "";
    this.name = "";
    this.kana = "";
    this.tel = "";
    this.mail = "";
    this.confirmMail = "";

    this.standardUserFunctionsModel = {
      functions: {
        /** 家族連絡サービス */
        facilitiesFunction: false,
        roomsFunction: false,
        useFamilyFunctionsFunction: false,
        individuallyUseFamilyFunctionsFunction: false,
        sendMessageGroupsFunction: false,
        careimagesFunction: false,
        eventsFunction: false,
        familiesFunction: false,
        familyMessagesFunction: false,
        otherUserGroupsFunction: false,
        picturesFunction: false,
        receiveMessagesFunction: false,
        reservationsFunction: false,
        outingsFunction: false,
        residentsFunction: false,
        sendMessagesFunction: false,
        reportsFunction: false,
        safetyConfirmationFunction: false,
        sendDocumentsFunction: false,
        roomSchedulesFunction: false,
        /** 待機者サービス */
        useWaiterService: false,
        waiterFollowersFunction: false,
        waiterSendMessagesFunction: false,
        inspectionReservationsFunction: false,
        recruitmentContactsFunction: false,
        waitersFunction: false,
        useOtherUserFunctionsFunction: false,
        questionnairesFunction: false,
        waiterReceiveMessagesFunction: false,
        /** 他事業所連携 */
        useOtherOfficeConnectionService: false,
        otherOfficeTopPageFunction: false,
        otherOfficeMessagesFunction: false,
        otherOfficeOnlineMeetingsFunction: false,
        otherOfficeRoomSchedulesFunction: false
      },
      reservationsFunction: {
        useEditReservations: true,
        useOnlineReservationSchedules: true,
        useOnlineReservationSettings: true,
        useReservationSchedules: true,
        useReservationSettings: true,
        useReserveInstead: true,
        useStartOnlineReservations: true
      },
      sendMessagesFunction: {
        useBasicSetting: true,
        useTemplate: true,
        useSaveDraftMessage: true,
        useSendAndReply: true,
        useSendTargetToAllFamily: true,
        useSendTargetToFamily: true,
        useSendTargetToAllWaiter: true,
        useSendTargetToWaiter: true,
        useSendTargetToOtherUser: true,
        useSendTargetToFamilySendMessageGroup: true,
        useSendTargetToOtherUserSendMessageGroup: true,
        useSendTargetToResidentGroup: true,
        useSendTargetToOtherUserGroup: true
      },
      receiveMessagesFunction: {
        showFamilyMessages: true,
        showOtherUserMessages: true,
        useFamilyReply: true,
        useOtherUserReply: true
      },
      waiterReceiveMessagesFunction: {
        showMessages: true,
        useReply: true
      },
      sendDocumentsFunction: {
        useEdit: true,
        useTemplate: true,
        useDraft: true,
        useControl: true,
        showTargetToAllFamily: true,
        showTargetToFamily: true,
        showTargetToOtherUser: true,
        showTargetToFamilySendMessageGroup: true,
        showTargetToOtherUserSendMessageGroup: true,
        showTargetToResidentGroup: true,
        showTargetToOtherUserGroup: true
      }
    };

    // 権限の利用者グループの選択肢を作成する
    const residentGroups: Array<ResidentGroupsGetModel> = await this.getResidentGroups();
    const permissionResidentGroups: Array<PermissionResidentGroupModel> = this.createResidentGroups(residentGroups);
    this.residentGroups = permissionResidentGroups;

    // 権限の施設関係者グループの選択肢を作成する
    const otherUserGroupTypes: Array<OtherUserGroupTypeModel> = await this.getOtherUserGroupTypes();
    const otherUserGroupTypeNameMap: Map<number, string> = this.createOtherUserGroupTypeNameMap(otherUserGroupTypes);
    const otherUserGroups: Array<OtherUserGroupModel> = await this.getOtherUserGroups();
    const permissionOtherUserGroups: Array<PermissionOtherUserGroupModel> = this.createOtherUserGroups(otherUserGroups, otherUserGroupTypeNameMap);
    this.otherUserGroups = permissionOtherUserGroups;

    this.showMessages = ShowMessage.ALL;

    this.isBusy = false;

    return;
  }

  /**
   * ### 全ての利用者グループを取得する
   */
  public async getResidentGroups(): Promise<Array<ResidentGroupsGetModel>> {
    const response: Array<ResidentGroupsGetModel> = await this.residentGroupsService.gets(userStore);
    return response;
  }

  /**
   * ### 権限の利用者グループの選択肢を作成する
   */
  public createResidentGroups(groups: Array<ResidentGroupsGetModel>): Array<PermissionResidentGroupModel> {
    const residentGroups: Array<PermissionResidentGroupModel> = [];
    for (const group of groups) {
      const isTarget: boolean = group.id == 1 ? true : false;
      residentGroups.push({
        groupId: group.id,
        name: group.name,
        isTarget: isTarget
      });
    }
    return residentGroups;
  }

  /**
   * ### 施設関係者グループ種類を取得する
   */
  public async getOtherUserGroupTypes(): Promise<Array<OtherUserGroupTypeModel>> {
    const response: Array<OtherUserGroupTypeModel> = await this.otherUserGroupTypesService.get(userStore);
    return response;
  }

  /**
   * ### 権限の利用者グループの選択肢を作成する
   */
  public createOtherUserGroups(groups: Array<OtherUserGroupModel>, otherUserGroupTypeNameMap: Map<number, string>): Array<PermissionOtherUserGroupModel> {
    const otherUserGroups: Array<PermissionOtherUserGroupModel> = [];
    for (const group of groups) {
      const typeName: string | undefined = otherUserGroupTypeNameMap.get(group.groupTypeId);
      const groupName: string = typeName ? `${typeName}・${group.name}` : group.name;
      otherUserGroups.push({
        groupId: group.id,
        name: groupName,
        isTarget: false
      });
    }
    return otherUserGroups;
  }

  /**
   * ### 施設関係者グループ種類IDと施設関係者グループ種類名のMAPを作成する
   */
  public createOtherUserGroupTypeNameMap(otherUserGroupTypes: Array<OtherUserGroupTypeModel>): Map<number, string> {
    const typeMap: Map<number, string> = new Map<number, string>();
    for (const type of otherUserGroupTypes) {
      typeMap.set(type.id, type.name);
    }
    return typeMap;
  }

  /**
   * ### 施設関係者グループを取得する
   */
  public async getOtherUserGroups(): Promise<Array<OtherUserGroupModel>> {
    let response: Array<OtherUserGroupModel> = [];

    try {
      response = await this.otherUserGroupsService.gets(userStore);
    } catch (error) {
      const systemerror: SystemError = error as SystemError;
      // 413エラーの場合、全件取得した結果がペイロード制限に引っかかったことになるので、index指定で段階的に取得する
      if (systemerror.statusCode == 413) {
        const count: number = await this.otherUserGroupsService.getCount(userStore);
        const loopEnd: number = Math.ceil(count / 300);

        for (let i = 0; i < loopEnd; i++) {
          const params: any = {
            startIndex: i * 300,
            endIndex: i * 300 + 300
          };
          response = response.concat(await this.otherUserGroupsService.gets(userStore, params));
        }
      }
    }
    return response;
  }

  /**
   * ### ユーザーID（ログインID）の変更イベント
   */
  public async onChangeUserId(): Promise<void> {
    // ユーザーIDをクリア（すべて消した）場合はチェック不要なので何もしない
    if (!this.userId) {
      return;
    }

    this.isBusy = true;
    this.loadingMessage = "ユーザーIDの確認中...";

    const exist: { exist: boolean; existAccount: boolean } = await this.standardAccountsService.exist(userStore, this.userId);
    this.registerChecked = true;
    this.userIdChecked = true;

    // 既に本施設に登録されている場合はモーダルを表示して登録ボタンを非活性化する
    if (exist.existAccount == true) {
      this.showDisableRegisterModal = true;
      this.disableRegister = true;
    }
    // 既に登録されている場合はモーダルを表示してパスワードを非活性化する
    else if (exist.exist == true) {
      this.showDisablePasswordModal = true;

      this.disableRegister = false;
      this.existAccount = exist.exist;
    } else {
      this.disableRegister = false;
      this.existAccount = exist.exist;
    }

    this.isBusy = false;
    return;
  }

  /**
   * ### 登録確認モーダルを表示する
   *
   * - 表示前にバリデーションチェックして、エラーならモーダルは表示しない
   * - バリデーションでOKかつ、ユーザーIDの存在チェックをしていない場合はチェックする
   */
  public async showConfirmUpdateModal(): Promise<void> {
    const validation: boolean = await this.$validator.validateAll();
    if (validation) {
      this.showValidationAlert = false;
      if (this.registerChecked == false || this.userIdChecked == false) {
        await this.onChangeUserId();
      }
      if (this.disableRegister == false) {
        this.showRegisterModal = true;
      }
    } else {
      this.showValidationAlert = true;
    }
    return;
  }

  /**
   * ### 一般ユーザーを登録する
   */
  public async registerAccount(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "登録中...";

    try {
      const requestBody: StandardAccountsPostModel = this.createRequestBody();
      await this.standardAccountsService.post(userStore, requestBody);

      // 登録成功ダイアログを表示する
      this.showRegisterSuccessModal = true;
    } catch (error) {
      const systemError: SystemError = error as SystemError;
      if (systemError.statusCode == 409) {
        this.errorMessage = "既に同一のユーザーIDが登録されているため登録できません";
      }
      // その他のステータスコードの場合は上位に例外を投げなおす
      else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    return;
  }

  /**
   * ### リクエストボディを作成する
   */
  public createRequestBody(): StandardAccountsPostModel {
    // 対象とされた権限(利用者グループ)を集計する
    const permissionsResidentGroup: number[] = [];
    for (const group of this.residentGroups) {
      if (group.isTarget == true) {
        permissionsResidentGroup.push(group.groupId);
      }
    }
    // 対象とされた権限(施設関係者グループ)を集計する
    const permissionsOtherUserGroup: number[] = [];
    for (const group of this.otherUserGroups) {
      if (group.isTarget == true) {
        permissionsOtherUserGroup.push(group.groupId);
      }
    }
    return {
      userId: this.userId,
      password: this.existAccount ? undefined : this.password,
      needRegisterForCognito: this.existAccount ? false : true,
      name: this.existAccount ? undefined : this.name,
      kana: this.existAccount ? undefined : this.kana,
      tel: this.existAccount ? undefined : this.tel,
      mail: this.existAccount ? undefined : this.mail,
      permissions: {
        ...(this.standardUserFunctionsModel as StandardUserFunctionsModel),
        residentGroups: permissionsResidentGroup,
        otherUserGroups: permissionsOtherUserGroup,
        showMessages: this.showMessages
      }
    };
  }

  /**
   * ### 一般ユーザー一覧に遷移する
   */
  public gotoStandardAccountList(): void {
    this.$router.push(PcUrl.StandardAccountList);
    return;
  }
}
</script>

<style lang="scss" scoped>
// 親要素からネストしてスタイルを指定する
#standard-account-register {
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;

  .update-btn,
  .cancel-btn {
    width: 120px;
  }
}
</style>
